import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Eye, CheckCircle, XCircle, MessageSquare, Clock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useBuyerNotifications, getNotificationStyle, type BuyerNotification } from '@/hooks/useBuyerNotifications';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { NotificationDetailDialog } from './NotificationDetailDialog';

const NotificationIcon = ({ type }: { type: BuyerNotification['notification_type'] }) => {
  const iconClass = "h-4 w-4";
  
  switch (type) {
    case 'viewed':
      return <Eye className={cn(iconClass, "text-blue-500")} />;
    case 'approved':
      return <CheckCircle className={cn(iconClass, "text-green-500")} />;
    case 'denied':
      return <XCircle className={cn(iconClass, "text-destructive")} />;
    case 'counter':
      return <MessageSquare className={cn(iconClass, "text-amber-500")} />;
    case 'expired':
      return <Clock className={cn(iconClass, "text-muted-foreground")} />;
    default:
      return <Bell className={cn(iconClass, "text-foreground")} />;
  }
};

export function NotificationBell() {
  const { user, isDealer } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<BuyerNotification | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const {
    notifications,
    isLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    isMarkingAllRead,
  } = useBuyerNotifications(user?.id || null);

  // Don't show notification bell for dealers (they have their own dashboard)
  if (!user || isDealer) {
    return null;
  }

  const handleNotificationClick = (notification: BuyerNotification, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Close dropdown and open detail dialog
    setIsOpen(false);
    setSelectedNotification(notification);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setSelectedNotification(null);
    }
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="font-semibold text-sm">Notifications</span>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-2 text-xs"
                onClick={() => markAllAsRead()}
                disabled={isMarkingAllRead}
              >
                <Check className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
          
          <ScrollArea className="h-[300px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground text-sm">
                <Bell className="h-8 w-8 mb-2 opacity-50" />
                <span>No notifications yet</span>
              </div>
            ) : (
              notifications.map((notification) => {
                const style = getNotificationStyle(notification.notification_type);
                
                return (
                  <DropdownMenuItem
                    key={notification.id}
                    className={cn(
                      "flex items-start gap-3 p-3 cursor-pointer focus:bg-muted",
                      !notification.is_read && "bg-primary/5"
                    )}
                    onClick={(e) => handleNotificationClick(notification, e)}
                  >
                    <div className={cn("p-2 rounded-full shrink-0", style.bgColor)}>
                      <NotificationIcon type={notification.notification_type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "font-medium text-sm truncate",
                          !notification.is_read && "text-foreground"
                        )}>
                          {notification.title}
                        </span>
                        {!notification.is_read && (
                          <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {notification.message}
                      </p>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </DropdownMenuItem>
                );
              })
            )}
          </ScrollArea>
          
          <DropdownMenuSeparator />
          <div className="p-2">
            <Button variant="ghost" size="sm" className="w-full" asChild>
              <Link to="/account" onClick={() => setIsOpen(false)}>
                View all deals
              </Link>
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <NotificationDetailDialog
        notification={selectedNotification}
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
      />
    </>
  );
}
